import React, { useMemo } from 'react';

import WithConfirm from '~/components/ConfirmModal';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import SortableTable from '~/components/core/Tables/SortableTable';
import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import ArrowDownwardIcon from '~/components/icons/ArrowDownwardIcon';
import ArrowUpwardIcon from '~/components/icons/ArrowUpwardIcon';
import PencilIcon from '~/components/icons/PencilIcon';
import TrashIcon from '~/components/icons/TrashIcon';
import { TEXTS } from '~/components/SystemConfiguration/DelegatedAuthority/texts';
import type { SysconfigOrganization } from '~/components/SystemConfiguration/SystenConfigurationTypes';
import Switch from '~/components/ui/atoms/Switch';
import type { OverrideRuleWithOverridesInfo } from '~/server_shared/openapi/data-contracts';
import { subOrgIdToNameDict } from '~/Utils';
import { getLobDescription } from '~/Utils/lobUtils';

/** Named rather than inferred: the sub-organizations column is spliced in conditionally, and an
 *  inferred union of the literal column shapes rejects any entry the first ones did not already
 *  describe. Mirrors the guardrail rules table. */
interface Column {
  id: string;
  label: string;
  disableSort?: boolean;
  width?: string;
  specialCell?: (row: OverrideRuleWithOverridesInfo) => React.ReactNode;
}

interface OverrideRulesTableProps {
  /** The configured organization (the tab's `useSysconfig()` value), taken as a prop the way the
   *  guardrail rules table takes it. */
  organization: SysconfigOrganization;
  rules: OverrideRuleWithOverridesInfo[];
  onEdit: (rule: OverrideRuleWithOverridesInfo) => void;
  onDelete: (rule: OverrideRuleWithOverridesInfo) => void;
  onToggleEnabled: (rule: OverrideRuleWithOverridesInfo, nextEnabled: boolean) => void;
  onMoveUp: (rule: OverrideRuleWithOverridesInfo) => void;
  onMoveDown: (rule: OverrideRuleWithOverridesInfo) => void;
  disabled?: boolean;
}

const OverrideRulesTable: React.FC<OverrideRulesTableProps> = ({
  organization,
  rules,
  onEdit,
  onDelete,
  onToggleEnabled,
  onMoveUp,
  onMoveDown,
  disabled = false,
}) => {
  const { lobConfigurationsDict } = useLobConfiguration();
  // Identical for every row — build once per `organization`, not per cell render.
  const subOrgIdToName = useMemo(() => subOrgIdToNameDict(organization), [organization]);

  if (rules.length === 0) {
    return <div className="py-12 text-center text-slate-700">{TEXTS.OVERRIDE_RULES_EMPTY_STATE}</div>;
  }

  const columns: Column[] = [
    { id: 'display_name', label: TEXTS.COLUMN_RULE_NAME },
    {
      id: 'lob',
      label: TEXTS.COLUMN_LOB,
      disableSort: true,
      // Only an explicit `true` is "all" — the engine's reading, and the upsert dialog's.
      specialCell: (row: OverrideRuleWithOverridesInfo) =>
        row.is_all_lobs === true
          ? TEXTS.SCOPE_ALL
          : (row.lobs ?? []).map((lobKey) => getLobDescription(lobKey, lobConfigurationsDict)).join(', '),
    },
    {
      id: 'is_enabled',
      label: TEXTS.COLUMN_ENABLED,
      width: '100px',
      disableSort: true,
      specialCell: (row: OverrideRuleWithOverridesInfo) => (
        <Switch
          checked={row.is_enabled}
          onChange={(_event, checked) => onToggleEnabled(row, checked)}
          disabled={disabled}
          aria-label={`${TEXTS.COLUMN_ENABLED} ${row.display_name}`}
        />
      ),
    },
    {
      id: 'priority',
      label: TEXTS.COLUMN_PRIORITY,
      width: '110px',
      disableSort: true,
      specialCell: (row: OverrideRuleWithOverridesInfo) => (
        <div className="flex items-center gap-4">
          <IconButton
            onClick={() => onMoveUp(row)}
            disabled={disabled || row.rule_id === rules[0].rule_id}
            aria-label={TEXTS.MOVE_RULE_UP_LABEL(row.display_name)}
            className="disabled:opacity-40"
          >
            <ArrowUpwardIcon />
          </IconButton>
          <IconButton
            onClick={() => onMoveDown(row)}
            disabled={disabled || row.rule_id === rules[rules.length - 1].rule_id}
            aria-label={TEXTS.MOVE_RULE_DOWN_LABEL(row.display_name)}
            className="disabled:opacity-40"
          >
            <ArrowDownwardIcon />
          </IconButton>
        </div>
      ),
    },
    {
      id: 'actions',
      label: '',
      width: '100px',
      disableSort: true,
      specialCell: (row: OverrideRuleWithOverridesInfo) => (
        <div className="flex gap-4">
          <IconButton
            onClick={() => onEdit(row)}
            disabled={disabled}
            aria-label={`${TEXTS.EDIT_OVERRIDE_RULE} ${row.display_name}`}
          >
            <PencilIcon />
          </IconButton>
          <WithConfirm
            title={TEXTS.DELETE_OVERRIDE_RULE_CONFIRM_TITLE}
            contentText={TEXTS.DELETE_OVERRIDE_RULE_CONFIRM_BODY(row.display_name)}
            primaryButtonName={TEXTS.DELETE_LEVEL}
            shouldCloseOnPrimary
          >
            <IconButton
              onClick={() => onDelete(row)}
              disabled={disabled}
              aria-label={`${TEXTS.DELETE_OVERRIDE_RULE_CONFIRM_TITLE} ${row.display_name}`}
            >
              <TrashIcon />
            </IconButton>
          </WithConfirm>
        </div>
      ),
    },
  ];

  if (organization.sub_organizations_enabled) {
    columns.splice(1, 0, {
      id: 'sub_organization_ids',
      label: TEXTS.COLUMN_SUB_ORGANIZATIONS,
      disableSort: true,
      specialCell: (row: OverrideRuleWithOverridesInfo) =>
        (row.is_all_sub_orgs === true
          ? Object.values(subOrgIdToName)
          : (row.sub_organization_ids ?? []).map((subOrgId) => subOrgIdToName[subOrgId]).filter(Boolean)
        ).join(', '),
    });
  }

  // SortableTable keys rows by `row.id`; OverrideRuleWithOverridesInfo uses `rule_id` instead.
  const rows = rules.map((rule) => ({ ...rule, id: rule.rule_id }));
  return <SortableTable columns={columns} rows={rows} disableSortByUser keepRowsOrder />;
};

export default OverrideRulesTable;
